import { usePubSub } from "@videosdk.live/react-sdk";
import { useCallback, useEffect, useRef, useState } from "react";

// Live map of the customer's phone location during the call.
// The customer web app publishes on the VideoSDK PubSub topic "LOCATION" (scoped to this meeting):
//   { type: "location", lat, lng, acc, heading, speed, facing, ts }   or   { type: "location", error, ts }
// facing = compass direction the phone's back camera points (0 = north); heading = GPS course while walking.
// Leaflet is loaded from the CDN on first use, with OpenStreetMap tiles.

const LEAFLET_VER = "1.9.4";
const LEAFLET_CSS = `https://cdnjs.cloudflare.com/ajax/libs/leaflet/${LEAFLET_VER}/leaflet.min.css`;
const LEAFLET_JS = `https://cdnjs.cloudflare.com/ajax/libs/leaflet/${LEAFLET_VER}/leaflet.min.js`;
const STALE_MS = 60000;
const TRAIL_MAX = 200;

let leafletPromise = null;
function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (!leafletPromise) {
    leafletPromise = new Promise((resolve, reject) => {
      const css = document.createElement("link");
      css.rel = "stylesheet";
      css.href = LEAFLET_CSS;
      document.head.appendChild(css);
      const js = document.createElement("script");
      js.src = LEAFLET_JS;
      js.onload = () => resolve(window.L);
      js.onerror = () => { leafletPromise = null; reject(new Error("map library failed to load")); };
      document.head.appendChild(js);
    });
  }
  return leafletPromise;
}

const ERRORS = {
  denied: "Customer blocked location access. Ask them to allow location for this site in their browser settings and rejoin.",
  unsupported: "Customer's browser can't share location.",
  timeout: "Customer's phone couldn't get a GPS fix yet.",
  unavailable: "Customer's phone couldn't get a location (GPS off?).",
};

// Which way to point the marker: the compass, else the walking direction, else none (plain dot).
const direction = (l) => (typeof l.facing === "number" ? l.facing : typeof l.heading === "number" ? l.heading : null);
const COMPASS = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
const compassName = (deg) => COMPASS[Math.round(deg / 45) % 8];

function markerIcon(L, deg) {
  const html = deg == null
    ? '<div style="width:16px;height:16px;border-radius:50%;background:#e11d48;border:3px solid #fff;box-shadow:0 0 4px rgba(0,0,0,.5)"></div>'
    : `<svg width="40" height="40" viewBox="0 0 40 40" style="transform:rotate(${deg}deg);filter:drop-shadow(0 0 2px rgba(0,0,0,.5))">` +
      '<path d="M20 3 L31 34 L20 27 L9 34 Z" fill="#e11d48" stroke="#fff" stroke-width="2.5" stroke-linejoin="round"/></svg>';
  return L.divIcon({
    className: "",
    html: `<div style="width:40px;height:40px;display:flex;align-items:center;justify-content:center">${html}</div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
}

function ago(ts, now) {
  const s = Math.max(0, Math.round((now - ts) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  return m < 60 ? `${m} min ago` : `${Math.round(m / 60)} h ago`;
}

export function LocationPanel() {
  const [loc, setLoc] = useState(null);
  const [err, setErr] = useState(null);
  const [mapErr, setMapErr] = useState(null);
  const [open, setOpen] = useState(true);
  const [follow, setFollow] = useState(true);
  const [now, setNow] = useState(Date.now());
  const boxRef = useRef(null);
  const mapRef = useRef(null);
  const layersRef = useRef({});
  const trailRef = useRef([]);
  const lastTsRef = useRef(0);

  const onMessage = useCallback((msg) => {
    let data;
    try { data = JSON.parse(msg.message); } catch (_) { return; }
    if (!data || data.type !== "location" || (data.ts || 0) < lastTsRef.current) return;
    lastTsRef.current = data.ts || 0;
    if (data.error) { setErr(data.error); return; }
    if (typeof data.lat !== "number" || typeof data.lng !== "number") return;
    setErr(null);
    trailRef.current = [...trailRef.current, [data.lat, data.lng]].slice(-TRAIL_MAX);
    setLoc(data);
  }, []);

  usePubSub("LOCATION", {
    onMessageReceived: onMessage,
    onOldMessagesReceived: (msgs) => (msgs || []).forEach(onMessage),
  });

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(t);
  }, []);

  // Create the map once the panel is open and we have a first fix.
  useEffect(() => {
    if (!open || !loc || mapRef.current || !boxRef.current) return;
    let cancelled = false;
    loadLeaflet().then((L) => {
      if (cancelled || mapRef.current || !boxRef.current) return;
      const map = L.map(boxRef.current, { zoomControl: true, attributionControl: true }).setView([loc.lat, loc.lng], 17);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: "© OpenStreetMap contributors",
      }).addTo(map);
      layersRef.current = {
        trail: L.polyline(trailRef.current, { color: "#0BAFAF", weight: 3, opacity: 0.7 }).addTo(map),
        acc: L.circle([loc.lat, loc.lng], { radius: loc.acc || 0, color: "#0BAFAF", weight: 1, fillOpacity: 0.12 }).addTo(map),
        dot: L.marker([loc.lat, loc.lng], { icon: markerIcon(L, direction(loc)), interactive: false, keyboard: false }).addTo(map),
      };
      layersRef.current.dir = direction(loc);
      map.on("dragstart", () => setFollow(false));
      mapRef.current = map;
    }).catch((e) => setMapErr(e.message));
    return () => { cancelled = true; };
  }, [open, loc]);

  // Move the marker on every update.
  useEffect(() => {
    const map = mapRef.current, ly = layersRef.current;
    if (!map || !loc || !ly.dot) return;
    ly.dot.setLatLng([loc.lat, loc.lng]);
    const d = direction(loc);
    if (d !== ly.dir && window.L) { ly.dot.setIcon(markerIcon(window.L, d)); ly.dir = d; }
    ly.acc.setLatLng([loc.lat, loc.lng]).setRadius(loc.acc || 0);
    ly.trail.setLatLngs(trailRef.current);
    if (follow) map.panTo([loc.lat, loc.lng], { animate: true });
  }, [loc, follow]);

  // Drop the map when the panel collapses (Leaflet needs a visible container).
  useEffect(() => {
    if (open) return;
    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; layersRef.current = {}; }
  }, [open]);
  useEffect(() => () => { if (mapRef.current) mapRef.current.remove(); }, []);

  const stale = loc && now - loc.ts > STALE_MS;
  const live = loc && !stale && !err;
  const gmaps = loc && `https://www.google.com/maps?q=${loc.lat},${loc.lng}`;

  const box = {
    position: "fixed", top: 72, left: 12, zIndex: 20, width: open ? 320 : "auto",
    background: "rgba(255,255,255,.97)", color: "#1b2733", borderRadius: 10,
    boxShadow: "0 4px 18px rgba(0,0,0,.25)", fontSize: 13, overflow: "hidden",
  };

  return (
    <div style={box}>
      <div
        onClick={() => setOpen(!open)}
        style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", cursor: "pointer", borderBottom: open ? "1px solid #e3e8ec" : 0 }}
      >
        <span style={{ width: 8, height: 8, borderRadius: 4, background: live ? "#16a34a" : err ? "#dc2626" : "#9aa5ae" }} />
        <b style={{ flex: 1 }}>Customer location</b>
        {loc && <span style={{ fontSize: 11, color: stale ? "#b45309" : "#5b6670" }}>{ago(loc.ts, now)}</span>}
        <span>{open ? "▾" : "▸"}</span>
      </div>

      {open && (
        <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 8 }}>
          {err && <div style={{ color: "#b91c1c" }}>{ERRORS[err] || ERRORS.unavailable}</div>}
          {!loc && !err && <div style={{ color: "#5b6670" }}>Waiting for the customer's phone to share its location…</div>}
          {mapErr && <div style={{ color: "#b91c1c" }}>Map couldn't load ({mapErr}). Use the Google Maps link below.</div>}
          {loc && <div ref={boxRef} style={{ width: "100%", height: 220, borderRadius: 8, background: "#e8eef1" }} />}
          {loc && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <span style={{ fontFamily: "monospace" }}>{loc.lat.toFixed(6)}, {loc.lng.toFixed(6)}</span>
                <span style={{ color: loc.acc > 50 ? "#b45309" : "#5b6670" }}>±{loc.acc} m</span>
              </div>
              <div style={{ color: "#5b6670" }}>
                {typeof loc.facing === "number"
                  ? <>Camera facing <b style={{ color: "#1b2733" }}>{compassName(loc.facing)}</b> ({loc.facing}°)</>
                  : typeof loc.heading === "number"
                    ? <>Walking towards <b style={{ color: "#1b2733" }}>{compassName(loc.heading)}</b> ({loc.heading}°)</>
                    : "Facing direction not available (phone compass off or not allowed)"}
              </div>
              {stale && <div style={{ color: "#b45309" }}>No update for {ago(loc.ts, now).replace(" ago", "")}. Last known position shown.</div>}
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <a href={gmaps} target="_blank" rel="noreferrer" style={{ color: "#0b6b6b", fontWeight: 600 }}>Open in Google Maps</a>
                {!follow && (
                  <button type="button" onClick={() => setFollow(true)} style={{ marginLeft: "auto", fontSize: 12, padding: "2px 8px", border: "1px solid #cfd8dc", borderRadius: 6, background: "#fff", cursor: "pointer" }}>
                    Re-centre
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
